import i18next from 'i18next';

const getLocale = () => {
  const language = String(i18next.language || localStorage.getItem('i18nextLng') || localStorage.getItem('language') || 'hu').split('-')[0];
  return ({hu: 'hu-HU', en: 'en-GB', de: 'de-DE'})[language] || language;
};

const startOfDay = value => new Date(value.getFullYear(), value.getMonth(), value.getDate());

export const getCrmContrastTextColor = color => {
  const value = String(color || '').trim().replace('#', '');
  const hex = value.length === 3 ? value.split('').map(character => character + character).join('') : value;
  if (!/^[0-9a-f]{6}$/i.test(hex)) return '#FFFFFF';

  const channels = [0, 2, 4].map(offset => parseInt(hex.slice(offset, offset + 2), 16) / 255)
    .map(channel => channel <= 0.04045 ? channel / 12.92 : Math.pow((channel + 0.055) / 1.055, 2.4));
  const luminance = 0.2126 * channels[0] + 0.7152 * channels[1] + 0.0722 * channels[2];
  const whiteContrast = 1.05 / (luminance + 0.05);
  const darkContrast = (luminance + 0.05) / 0.057;

  return whiteContrast >= darkContrast ? '#FFFFFF' : '#111827';
};

export const formatCrmDateTime = (value, t) => {
  if (!value) return '—';
  const date = value instanceof Date ? value : new Date(value);
  if (Number.isNaN(date.getTime())) return String(value);

  const today = startOfDay(new Date());
  const day = startOfDay(date);
  const difference = Math.round((day.getTime() - today.getTime()) / 86400000);
  const time = new Intl.DateTimeFormat(getLocale(), {hour: '2-digit', minute: '2-digit'}).format(date);

  if (difference === 0) return `${t('crmDateToday')} ${time}`;
  if (difference === -1) return `${t('crmDateYesterday')} ${time}`;
  if (difference === 1) return `${t('crmDateTomorrow')} ${time}`;

  const datePart = new Intl.DateTimeFormat(getLocale(), {
    year: date.getFullYear() === today.getFullYear() ? undefined : 'numeric',
    month: 'short',
    day: 'numeric'
  }).format(date);
  return `${datePart} ${time}`;
};

export const getCrmTaskStatusCategory = category => {
  if (typeof category === 'string') return category;
  return category?.value || category?.name || '';
};

export const translateCrmTaskStatusCategory = (category, t) => {
  const keys = {
    TODO: 'crmTaskStatusCategoryTodo',
    IN_PROGRESS: 'crmTaskStatusCategoryInProgress',
    DONE: 'crmTaskStatusCategoryDone'
  };
  const value = getCrmTaskStatusCategory(category);
  return keys[value] ? t(keys[value]) : value;
};

export const translateCrmHistoryEvent = (type, t) => {
  const keys = {
    CREATED: 'crmHistoryEventCreated',
    FIELD_CHANGED: 'crmHistoryEventFieldChanged',
    COMMENT_ADDED: 'crmHistoryEventCommentAdded',
    CONVERTED: 'crmHistoryEventConverted',
    CREATED_FROM_LEAD: 'crmHistoryEventCreatedFromLead',
    POSTPONED: 'crmHistoryEventPostponed'
  };
  return keys[type] ? t(keys[type]) : type;
};

export const translateCrmHistoryField = (field, t) => {
  const keys = {
    status: 'crmHistoryFieldStatus',
    employee: 'crmHistoryFieldEmployee',
    deadline: 'crmHistoryFieldDeadline',
    stage: 'crmHistoryFieldStage',
    pipeline: 'crmHistoryFieldPipeline',
    owner: 'crmHistoryFieldOwner',
    amount: 'crmHistoryFieldAmount',
    probability: 'crmHistoryFieldProbability',
    expectedCloseDate: 'crmHistoryFieldExpectedCloseDate',
    nextActionAt: 'crmHistoryFieldNextAction'
  };
  return keys[field] ? t(keys[field]) : field;
};

export const formatCrmHistoryValue = (value, field, t) => {
  if (!value) return '—';
  if (['deadline', 'expectedCloseDate', 'nextActionAt'].includes(field)) return formatCrmDateTime(value, t);
  return value;
};

export const translateCrmTaskColumn = (column, t) => {
  const keys = {OVERDUE: 'crmTaskColumnOverdue', TODAY: 'crmTaskColumnToday', UPCOMING: 'crmTaskColumnUpcoming', NO_DEADLINE: 'crmTaskColumnNoDeadline', DONE: 'crmTaskColumnDone'};
  return keys[column] ? t(keys[column]) : column;
};

export const translateCrmStageCategory = (category, t) => {
  const keys = {OPEN: 'crmStageCategoryOpen', WON: 'crmStageCategoryWon', LOST: 'crmStageCategoryLost'};
  return keys[category] ? t(keys[category]) : category;
};

export const translateCrmLeadStatus = (status, t) => {
  const keys = {
    NEW: 'crmLeadStatusNew', IN_PROGRESS: 'crmLeadStatusInProgress', POSTPONED: 'crmLeadStatusPostponed',
    CLOSED: 'crmLeadStatusClosed', CONTACTED: 'crmLeadStatusContacted', QUALIFIED: 'crmLeadStatusQualified',
    CONVERTED: 'crmLeadStatusConverted', DISQUALIFIED: 'crmLeadStatusDisqualified'
  };
  return keys[status] ? t(keys[status]) : status;
};
